import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isSameMonth,
  addDays,
  isSameDay,
} from "date-fns";
import { Button } from "./Button";
import chevironLeft from "$/cheviron-left.svg";
import chevironRight from "$/cheviron-right.svg";
import { twMerge } from "tailwind-merge";
import { useCallback, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { DatePicker } from "./DatePicker";
export const HOTEL = "호텔링";
const TODAY = new Date();
const week = ["일", "월", "화", "수", "목", "금", "토"];
interface DayProps {
  today: Date;
  day: Date;
  index: number;
  schedules?: CalendarNewProps["schedules"];
  direction?: boolean;
  dayOnClick?: (day: Date | null) => void;
  selectedDay?: Date | null;
  selectedDays?: Date[];
  multiSelect?: boolean;
  onChange?: (days: Date[] | Date | null) => void;
  disablePast?: boolean;
  minDate?: Date; // 최소 선택 가능 날짜
  maxDate?: Date; // 최대 선택 가능 날짜 (해당일 포함)
  readOnlyDates?: Date[]; // 읽기 전용으로 표시할 날짜들
  disabledDates?: Date[]; // 비활성화할 날짜들
}
type DurationType = "first" | "last" | "only" | undefined;
const Day = ({
  schedules,
  today,
  day,
  index,
  direction,
  dayOnClick,
  selectedDay,
  selectedDays,
  multiSelect,
  onChange,
  disablePast,
  minDate,
  maxDate,
  readOnlyDates,
  disabledDates,
}: DayProps) => {
  const ref = useRef<HTMLDivElement>(null);
  // const [duration, setDurationType] = useState<DurationType>();
  const isOtherMonth = useMemo(() => !isSameMonth(day, today), [day, today]);
  const schedule = useMemo(
    () => schedules?.[format(day, "yyyyMMdd")],
    [schedules, day]
  );
  const hasDot = useMemo(
    () => (schedule?.length ?? 0) > 0 && schedule?.every((x) => x !== HOTEL),
    [schedule]
  );
  const hasDuration = useMemo(
    () => schedule?.some((x) => x === HOTEL),
    [schedule]
  );
  const durationType: DurationType = useMemo(() => {
    if (hasDuration && schedules) {
      const isFirst =
        !schedules[format(addDays(day, -1), "yyyyMMdd")]?.includes(HOTEL);
      const isLast =
        !schedules[format(addDays(day, 1), "yyyyMMdd")]?.includes(HOTEL);

      if (isLast && isFirst) return "only";
      if (isFirst) return "first";
      if (isLast) return "last";
    }
    return undefined;
  }, [day, hasDuration, schedules]);

  const memoSchedule = useMemo(() => schedule?.slice(0, 2), [schedule]);
  const isPast = disablePast && day < new Date(new Date().setHours(0, 0, 0, 0));
  const isBeforeMinDate =
    minDate && day < new Date(minDate.setHours(0, 0, 0, 0));
  const isAfterMaxDate =
    maxDate &&
    new Date(day.getFullYear(), day.getMonth(), day.getDate()) >
      new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
  const isReadOnly =
    readOnlyDates?.some((readOnlyDate) => isSameDay(readOnlyDate, day)) ||
    false;
  const isDisabledDate =
    disabledDates?.some((disabledDate) => isSameDay(disabledDate, day)) ||
    false;
  const isDisabled =
    isPast || isBeforeMinDate || isAfterMaxDate || isDisabledDate;

  // 다중/단일 선택 여부
  const isSelected = multiSelect
    ? Array.isArray(selectedDays) && selectedDays.some((d) => isSameDay(d, day))
    : selectedDay && isSameDay(selectedDay, day);

  // 읽기 전용 날짜 선택 여부
  const isReadOnlySelected = isReadOnly;

  // 클릭 핸들러
  const handleDayClick = () => {
    if (isDisabled) return;
    if (multiSelect) {
      if (isSelected) {
        onChange?.((selectedDays || []).filter((d) => !isSameDay(d, day)));
      } else {
        onChange?.([...(selectedDays || []), day]);
      }
    } else {
      if (isSelected) {
        onChange?.(null);
        dayOnClick?.(null);
      } else {
        onChange?.(day);
        dayOnClick?.(day);
      }
    }
  };

  const isToday =
    day.getFullYear() === new Date().getFullYear() &&
    day.getMonth() === new Date().getMonth() &&
    day.getDate() === new Date().getDate();

  return (
    <motion.div
      initial={{
        opacity: 0,
        x: direction ? 100 : direction === false ? -100 : 0,
      }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: direction ? -100 : direction === false ? 100 : 0 }}
      className="relative h-20 border-b border-[#e9e9e9]"
      ref={ref}
      data-duration={hasDuration}
    >
      <p
        className={twMerge(
          "text-sm my-1 mx-auto w-8 h-8 text-center font-medium leading-8 cursor-pointer relative z-10 flex items-center justify-center transition-all",
          (isOtherMonth || isDisabled) && "!text-[#ccc]",
          isDisabled && "cursor-not-allowed",
          index === 6 && "text-[#4F6AD5]",
          index === 0 && "text-[#F24747]",
          isSelected
            ? "bg-yellow text-white rounded-full border-2 border-[#FF9934]"
            : isReadOnlySelected
              ? "bg-yellow text-white rounded-full border-2 border-[#FF9934]"
              : isToday
                ? "border-2 border-[#FF9934] text-[#FF9934] bg-white rounded-full"
                : ""
        )}
        onClick={handleDayClick}
      >
        {format(day, "d")}
      </p>
      {hasDot && (
        <i className="bg-blue w-1 h-1 rounded-full absolute top-8 left-1/2 -ml-0.5" />
      )}
      {hasDuration && (
        <i
          className={twMerge(
            "left-0 w-full h-5 absolute top-2.5 z-1 bg-yellow opacity-20",
            durationType === "only" && "rounded-full",
            durationType === "first" && "rounded-s-full",
            durationType === "last" && "rounded-e-full"
          )}
        />
      )}
      {memoSchedule?.map((x) => (
        <p
          className={twMerge(
            "text-xs text-center text-[#666]",
            x === HOTEL && "text-yellow"
          )}
          key={x}
        >
          {x}
        </p>
      ))}
    </motion.div>
  );
};
// const Header = ({
//   value,
//   onChange,
//   disabled,
// }: {
//   today: Date;
//   value: string;
//   disabled?: boolean;
//   onChange?: (value: string) => unknown;
// }) => {
//   return (

//   );
//   //<p className="text-base font-medium">{month}</p>;
// };
const Week = () => {
  return week.map((day) => (
    <span
      className="text-sm content-center h-8 text-center bg-[#f5f5f5] border-y border-[#e9e9e9]"
      key={day}
    >
      {day}
    </span>
  ));
};
interface CalendarNewProps {
  disabled?: boolean;
  defaultToday?: Date;
  schedules?: { [key: string]: string[] };
  label?: string;
  dayOnClick?: (day: Date | null) => void;
  selectedDay?: Date | null;
  selectedDays?: Date[];
  onChange?: (days: Date[] | Date | null) => void;
  multiSelect?: boolean;
  handleSelectDate?: (isNext: boolean) => void;
  disablePast?: boolean;
  minDate?: Date; // 최소 선택 가능 날짜
  maxDate?: Date; // 최대 선택 가능 날짜 (해당일 포함)
  readOnlyDates?: Date[]; // 읽기 전용으로 표시할 날짜들
  disabledDates?: Date[]; // 비활성화할 날짜들
}
export const CalendarNew = ({
  defaultToday = TODAY,
  disabled,
  label,
  schedules,
  dayOnClick,
  selectedDay,
  selectedDays,
  onChange,
  multiSelect,
  handleSelectDate,
  disablePast,
  minDate,
  maxDate,
  readOnlyDates,
  disabledDates,
}: CalendarNewProps) => {
  const direction = useRef<boolean>();
  const [currentDate, setCurrentDate] = useState(() => {
    const date = new Date(defaultToday);
    return {
      year: date.getFullYear(),
      month: date.getMonth(),
    };
  });

  const today = useMemo(() => {
    return new Date(currentDate.year, currentDate.month, 1);
  }, [currentDate]);

  const value = useMemo(() => format(today, "yyyy.MM"), [today]);
  const startDate = startOfWeek(startOfMonth(today));
  const endDate = endOfWeek(endOfMonth(today));
  const days = eachDayOfInterval({ start: startDate, end: endDate });

  const handlePrev = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    direction.current = false;
    setCurrentDate((prev) => {
      const newMonth = prev.month - 1;
      if (newMonth < 0) {
        return {
          year: prev.year - 1,
          month: 11,
        };
      }
      return {
        year: prev.year,
        month: newMonth,
      };
    });
    handleSelectDate?.(false);
  }, []);

  const handleNext = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    direction.current = true;
    setCurrentDate((prev) => {
      const newMonth = prev.month + 1;
      if (newMonth > 11) {
        return {
          year: prev.year + 1,
          month: 0,
        };
      }
      return {
        year: prev.year,
        month: newMonth,
      };
    });
    handleSelectDate?.(true);
  }, []);

  const handleChange = useCallback((value: string) => {
    if (!value) return;
    direction.current = undefined;
    const [yearStr, monthStr] = value.split(".");
    const year = parseInt(yearStr);
    const month = parseInt(monthStr) - 1;
    setCurrentDate({ year, month });
  }, []);

  return (
    <div className={twMerge("px-4 pt-2 bg-white rounded-lg", label && "mb-4")}>
      <label className="text-sm font-bold">{label}</label>
      <div
        className={twMerge(
          "flex items-center gap-10 m-auto w-fit",
          label && "mb-4"
        )}
      >
        <Button
          onClick={handlePrev}
          className={twMerge(disabled && "invisible")}
        >
          <img src={chevironLeft} alt="이전 달력" />
        </Button>
        <DatePicker
          className="p-0 text-base font-medium border-none"
          defaultValue={value}
          onChange={handleChange}
          disabled={true}
          showMonthYearPicker
        />
        <Button
          onClick={handleNext}
          className={twMerge(disabled && "invisible")}
        >
          <img src={chevironRight} alt="다음 달력" />
        </Button>
      </div>
      <div className="grid grid-cols-7">
        <Week />
        {days.map((day, index) => (
          <Day
            selectedDay={selectedDay}
            selectedDays={selectedDays}
            onChange={onChange}
            multiSelect={multiSelect}
            direction={direction.current}
            schedules={schedules}
            today={today}
            day={day}
            index={index % 7}
            dayOnClick={dayOnClick}
            disablePast={disablePast}
            minDate={minDate}
            maxDate={maxDate}
            readOnlyDates={readOnlyDates}
            disabledDates={disabledDates}
            key={day.toString() + index}
          />
        ))}
      </div>
    </div>
  );
};
